import { netWorth } from "../../lib/calculators/planning";
import { debtPayoff } from "../../lib/calculators/debt";
import { CAD } from "../../lib/format";

export const netWorthConfig = {
  slug: "net-worth-calculator-canada",
  name: "Net Worth Calculator",
  category: "debt",
  icon: "Scale",
  tagline: "Add up what you own, subtract what you owe.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "net-worth-tracker",
  relatedTools: ["debt-payoff-calculator-canada", "budget-calculator-canada"],
  seo: {
    title: "Net Worth Calculator Canada (2026) — Assets Minus Liabilities",
    description: "Calculate your net worth by listing your assets and debts. See a clear breakdown and track your financial progress in Canada.",
    keywords: ["net worth calculator canada", "assets minus liabilities", "how to calculate net worth", "personal net worth"],
  },
  inputs: [
    {
      id: "assets",
      label: "Assets (what you own)",
      type: "list",
      itemLabel: "asset",
      addLabel: "Add asset",
      default: [
        { label: "Cash & savings", value: 15000 },
        { label: "TFSA", value: 30000 },
        { label: "RRSP", value: 45000 },
        { label: "Home", value: 600000 },
        { label: "Vehicle", value: 20000 },
      ],
    },
    {
      id: "liabilities",
      label: "Liabilities (what you owe)",
      type: "list",
      itemLabel: "debt",
      addLabel: "Add debt",
      default: [
        { label: "Mortgage", value: 400000 },
        { label: "Car loan", value: 12000 },
        { label: "Credit cards", value: 3000 },
      ],
    },
  ],
  compute: (v) => {
    const r = netWorth({ assets: v.assets || [], liabilities: v.liabilities || [] });
    return {
      primary: { label: "Your net worth", value: r.netWorth, type: "currency" },
      secondary: [
        { label: "Total assets", value: r.totalAssets, type: "currency" },
        { label: "Total liabilities", value: r.totalLiabilities, type: "currency" },
        { label: "Debt-to-asset ratio", value: r.totalAssets ? `${((r.totalLiabilities / r.totalAssets) * 100).toFixed(0)}%` : "0%", type: "raw" },
      ],
      chart: {
        type: "pie",
        title: "Assets vs liabilities",
        data: [
          { name: "Assets", value: Math.round(r.totalAssets) },
          { name: "Liabilities", value: Math.round(r.totalLiabilities) },
        ],
      },
      downloadRows: () => [["Item", "Type", "Value"], ...r.breakdown.map((b) => [b.name, b.type, b.value])],
    };
  },
  content: {
    howItWorks: "Net worth is the single clearest snapshot of your financial health: total assets (everything you own) minus total liabilities (everything you owe). Track it every few months — a rising net worth means you're building wealth, even if individual months feel tight.",
    inputsExplained: [
      { term: "Assets", desc: "Cash, savings, investments (TFSA/RRSP), home, vehicle, and other valuables." },
      { term: "Liabilities", desc: "Mortgage, car loans, student loans, credit card balances and lines of credit." },
    ],
    example: "If you own $710,000 in assets and owe $415,000, your net worth is $295,000.",
    assumptions: ["Values are current market estimates.", "Everything is in Canadian dollars.", "Illiquid assets (like a home) are included at estimated value."],
    limitations: ["A home's value is an estimate until sold.", "Doesn't measure cash flow or income.", "Snapshot in time — track the trend, not one number."],
  },
  faq: [
    { q: "What's a good net worth?", a: "It varies by age and income. Focus on the trend: is your net worth growing year over year? That's the sign of financial progress." },
    { q: "Should I include my home?", a: "Yes, at its estimated market value, with the mortgage as a liability. Some people also track net worth excluding their home to see liquid wealth." },
  ],
};

export const debtPayoffConfig = {
  slug: "debt-payoff-calculator-canada",
  name: "Debt Payoff Calculator",
  category: "debt",
  icon: "Scale",
  tagline: "Compare the Avalanche and Snowball methods to get debt-free faster.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "debt-freedom-planner",
  relatedTools: ["net-worth-calculator-canada", "budget-calculator-canada"],
  seo: {
    title: "Debt Payoff Calculator Canada (2026) — Avalanche vs Snowball",
    description: "Compare the debt avalanche and snowball methods. See how fast you'll be debt-free and how much interest you'll save with extra payments.",
    keywords: ["debt payoff calculator canada", "avalanche vs snowball", "debt snowball calculator", "pay off debt faster"],
  },
  inputs: [
    { id: "extraPayment", label: "Extra monthly payment", type: "currency", default: 300, min: 0, help: "Money above minimum payments you can put toward debt each month." },
    {
      id: "debts",
      label: "Your debts",
      type: "list",
      itemLabel: "debt",
      addLabel: "Add debt",
      columns: [
        { key: "name", label: "Name", type: "text" },
        { key: "balance", label: "Balance", type: "currency" },
        { key: "rate", label: "Rate %", type: "number" },
        { key: "minPayment", label: "Min. payment", type: "currency" },
      ],
      default: [
        { name: "Credit card", balance: 8000, rate: 19.99, minPayment: 200 },
        { name: "Car loan", balance: 15000, rate: 6.5, minPayment: 350 },
        { name: "Line of credit", balance: 10000, rate: 9.5, minPayment: 150 },
      ],
    },
  ],
  compute: (v) => {
    const r = debtPayoff({ debts: v.debts || [], extraPayment: v.extraPayment });
    const rec = r.recommended === "avalanche" ? r.avalanche : r.snowball;
    return {
      primary: { label: `Debt-free in (${r.recommended})`, value: rec.years, type: "years" },
      secondary: [
        { label: "Total debt", value: r.totalDebt, type: "currency" },
        { label: "Avalanche: total interest", value: r.avalanche.totalInterest, type: "currency", hint: `${r.avalanche.years} yrs` },
        { label: "Snowball: total interest", value: r.snowball.totalInterest, type: "currency", hint: `${r.snowball.years} yrs` },
        { label: "Interest saved (avalanche)", value: r.interestSavedVsSnowball, type: "currency" },
        { label: "Recommended method", value: r.recommended === "avalanche" ? "Avalanche" : "Snowball", type: "raw" },
      ],
      chart: {
        type: "line",
        title: "Balance over time: Avalanche vs Snowball",
        data: (() => {
          const maxLen = Math.max(r.avalanche.series.length, r.snowball.series.length);
          const out = [];
          for (let i = 0; i < maxLen; i += Math.ceil(maxLen / 40) || 1) {
            out.push({
              x: `M${i}`,
              Avalanche: r.avalanche.series[i]?.balance ?? 0,
              Snowball: r.snowball.series[i]?.balance ?? 0,
            });
          }
          return out;
        })(),
        xKey: "x",
        series: [{ key: "Avalanche", name: "Avalanche" }, { key: "Snowball", name: "Snowball" }],
      },
      status: { type: "success", text: `The ${r.recommended} method pays off your debt with the least interest. It saves about ${CAD(r.interestSavedVsSnowball)} vs the other method.` },
    };
  },
  content: {
    howItWorks: "Both methods pay minimums on every debt, then throw all extra money at one target debt. The Avalanche method targets the highest interest rate first (mathematically cheapest). The Snowball method targets the smallest balance first (fastest wins for motivation). This tool simulates both month by month so you can compare time and interest.",
    inputsExplained: [
      { term: "Extra monthly payment", desc: "Anything you can pay above the combined minimums — this is what accelerates payoff." },
      { term: "Your debts", desc: "List each debt's balance, interest rate and minimum monthly payment." },
    ],
    example: "With $33,000 of debt and $300 extra per month, the avalanche method typically clears the debt a bit faster and saves the most interest.",
    assumptions: ["Interest compounds monthly on each debt.", "Extra payment stays constant.", "No new debt is added."],
    limitations: ["Assumes fixed rates and minimum payments.", "Doesn't model promotional 0% periods or fees.", "Snowball may be worth a small extra cost for motivation."],
  },
  faq: [
    { q: "Avalanche or snowball — which is better?", a: "Avalanche saves the most money by targeting high-interest debt first. Snowball can keep you motivated by clearing small debts quickly. Pick the one you'll actually stick with." },
    { q: "How much difference does extra payment make?", a: "A lot. Even a small consistent extra payment can cut years off your payoff and save thousands in interest — try changing the amount to see." },
  ],
};
